import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import useMessagesSocket from "./useMessagesSocket";
import useRealtimeGames from "./useRealtimeGames";
import {
  buildInboxItems,
  formatMessageTime,
  isProbablyGifUrl,
} from "../../utils/messages/messageHelpers";
import { giphyFetch, pickGiphyBestUrl } from "../../utils/messages/gifHelpers";

/** Socket.IO runs on the HTTP origin; strip a trailing `/api` used for REST. */
function resolveSocketOrigin(apiBaseRaw) {
  const trimmed = String(apiBaseRaw || "").trim();
  if (!trimmed) {
    if (process.env.NODE_ENV === "development") {
      return "http://localhost:5000";
    }
    return typeof window !== "undefined" ? window.location.origin : "";
  }
  return trimmed.replace(/\/api\/?$/i, "");
}

function toFiniteId(raw) {
  if (raw === undefined || raw === null) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export default function useMessagesData() {
  const { user } = useAuth();

  let storedUser = null;
  try {
    storedUser = JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    storedUser = null;
  }
  const effectiveUser = user || storedUser;
  const currentUserId = toFiniteId(effectiveUser?.id ?? effectiveUser?.user_id);

  const [friends, setFriends] = useState([]);
  const [dmInbox, setDmInbox] = useState([]);
  const [groups, setGroups] = useState([]);
  const [onlineFriends, setOnlineFriends] = useState(new Set());
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);

  const [directMessagesByFriend, setDirectMessagesByFriend] = useState({});
  const [groupMessages, setGroupMessages] = useState({});
  const [unreadCounts, setUnreadCounts] = useState({});
  const [unreadGroupCounts, setUnreadGroupCounts] = useState({});
  const [typingUsers, setTypingUsers] = useState(new Set());
  const [groupTypingUsers, setGroupTypingUsers] = useState({});

  const [inputMessage, setInputMessage] = useState("");
  const [isEmojiOpen, setIsEmojiOpen] = useState(false);
  const [isGifOpen, setIsGifOpen] = useState(false);
  const [gifQuery, setGifQuery] = useState("");
  const [gifItems, setGifItems] = useState([]);
  const [gifLoading, setGifLoading] = useState(false);
  const [gifError, setGifError] = useState("");
  const [fileUploadBusy, setFileUploadBusy] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [selectedGroupMembers, setSelectedGroupMembers] = useState([]);
  const [groupName, setGroupName] = useState("");

  const messageListRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const gifAbortRef = useRef(null);
  const gifDebounceRef = useRef(null);
  const prevMessageCountRef = useRef(0);
  const stickToBottomRef = useRef(true);

  const apiBase = process.env.REACT_APP_API_BASE || "";
  const socketOrigin = resolveSocketOrigin(apiBase);
  const giphyKey = process.env.REACT_APP_GIPHY_KEY || "";

  const apiUrl = useCallback(
    (path) => {
      const p = String(path || "");
      if (/^https?:\/\//i.test(p)) return p;
      return `${apiBase}${p.startsWith("/") ? p : `/${p}`}`;
    },
    [apiBase]
  );

  const selectedFriendId = useMemo(
    () => toFiniteId(selectedFriend?.user_id),
    [selectedFriend?.user_id]
  );
  const selectedGroupId = useMemo(
    () => toFiniteId(selectedGroup?.group_id),
    [selectedGroup?.group_id]
  );
  const isGroupChat = !!selectedGroup;

  const selectedFriendIdRef = useRef(selectedFriendId);
  const selectedGroupIdRef = useRef(selectedGroupId);
  useEffect(() => {
    selectedFriendIdRef.current = selectedFriendId;
    selectedGroupIdRef.current = selectedGroupId;
  }, [selectedFriendId, selectedGroupId]);

  const scrollToBottom = useCallback((behavior = "auto") => {
    const el = messageListRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTo({ top: el.scrollHeight, behavior });
    });
  }, []);

  const computeStickToBottom = useCallback(() => {
    const el = messageListRef.current;
    if (!el) return true;
    const threshold = 48;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    return distanceFromBottom <= threshold;
  }, []);

  const appendDirectMessage = useCallback((friendId, message) => {
    const id = toFiniteId(friendId);
    if (id == null || message?.id == null) return;
    const mid = Number(message.id);
    setDirectMessagesByFriend((prev) => {
      const cur = prev[id] || [];
      if (cur.some((m) => Number(m.id) === mid)) return prev;
      return { ...prev, [id]: [...cur, message] };
    });
  }, []);

  const games = useRealtimeGames({ currentUserId, socketOrigin });

  const socketRef = useMessagesSocket({
    currentUserId,
    socketOrigin,
    selectedFriendIdRef,
    selectedGroupIdRef,
    onFriendsOnlineStatus: (setOnline) => setOnlineFriends(setOnline),
    onFriendStatusChanged: ({ userId, isOnline }) => {
      setOnlineFriends((prev) => {
        const next = new Set(prev);
        if (isOnline) next.add(userId);
        else next.delete(userId);
        return next;
      });
    },
    onDirectMessage: ({ message, selectedFriendId, socket }) => {
      const sid = Number(message?.sender_id);
      const cid = Number(currentUserId);
      const otherId = Number.isFinite(sid) && Number.isFinite(cid) && sid === cid
        ? message?.recipient_id
        : message?.sender_id;
      const otherKey = toFiniteId(otherId);
      if (otherKey == null) return;

      appendDirectMessage(otherKey, message);
      if (
        selectedFriendId != null &&
        otherKey === selectedFriendId
      ) {
        socket.emit("mark-messages-read", { senderId: otherKey });
        setUnreadCounts((prev) => {
          const next = { ...prev };
          delete next[otherKey];
          delete next[otherId];
          return next;
        });
      } else if (Number.isFinite(sid) && sid !== cid) {
        setUnreadCounts((prev) => ({
          ...prev,
          [sid]: (prev[sid] || 0) + 1,
        }));
      }
    },
    onDirectMessageSent: (message) => {
      const sid = Number(message?.sender_id);
      const cid = Number(currentUserId);
      const otherId = Number.isFinite(sid) && Number.isFinite(cid) && sid === cid
        ? message?.recipient_id
        : message?.sender_id;
      const otherKey = toFiniteId(otherId);
      if (otherKey == null) return;
      appendDirectMessage(otherKey, message);
    },
    onFriendTyping: ({ userId, isTyping }) => {
      setTypingUsers((prev) => {
        const next = new Set(prev);
        if (isTyping) next.add(userId);
        else next.delete(userId);
        return next;
      });
    },
    onGroupMessage: ({ message, selectedGroupId: openGroupId }) => {
      const gid = toFiniteId(message?.group_id);
      if (gid == null || message?.id == null) return;
      const mid = Number(message.id);
      setGroupMessages((prev) => {
        const cur = prev[gid] || [];
        if (cur.some((m) => Number(m.id) === mid)) return prev;
        return { ...prev, [gid]: [...cur, message] };
      });
      const openId = toFiniteId(openGroupId);
      if (openId == null || gid !== openId) {
        setUnreadGroupCounts((prev) => ({
          ...prev,
          [gid]: (prev[gid] || 0) + 1,
        }));
      }
    },
    onGroupTyping: ({ groupId, username, isTyping }) => {
      setGroupTypingUsers((prev) => {
        const next = { ...prev };
        if (!next[groupId]) next[groupId] = new Set();
        else next[groupId] = new Set(next[groupId]);

        if (isTyping) next[groupId].add(username);
        else next[groupId].delete(username);
        if (next[groupId].size === 0) delete next[groupId];
        return next;
      });
    },
    onGroupMemberLeft: ({ groupId }) => {
      setGroups((prev) => prev.map((g) => (g.group_id === groupId ? { ...g } : g)));
    },
    onGroupDisbanded: ({ groupId }) => {
      setGroups((prev) => prev.filter((g) => g.group_id !== groupId));
      if (selectedGroupIdRef.current === groupId) setSelectedGroup(null);
      setGroupMessages((prev) => {
        const next = { ...prev };
        delete next[groupId];
        return next;
      });
    },
  });

  useEffect(() => {
    if (!currentUserId) return;
    axios
      .get(apiUrl(`/api/friends/${currentUserId}`), { withCredentials: true })
      .then((res) => setFriends(res.data || []))
      .catch((error) => console.error("[Messages] Error fetching friends:", error));
  }, [currentUserId, apiUrl]);

  useEffect(() => {
    if (!currentUserId) return;
    axios
      .get(apiUrl("/api/direct-messages/inbox"), { withCredentials: true })
      .then((res) => setDmInbox(res.data?.conversations || []))
      .catch((error) => console.error("[Messages] Error fetching DM inbox:", error));
  }, [currentUserId, apiUrl]);

  useEffect(() => {
    if (!currentUserId) return;
    axios
      .get(apiUrl("/api/groups"), { withCredentials: true })
      .then((res) => setGroups(res.data?.groups || []))
      .catch((error) => console.error("[Messages] Error fetching groups:", error));
  }, [currentUserId, apiUrl]);

  useEffect(() => {
    if (!currentUserId) return;
    axios
      .get(apiUrl("/api/direct-messages/unread/count"), { withCredentials: true })
      .then((res) => setUnreadCounts(res.data?.unreadCounts || {}))
      .catch((error) => console.error("[Messages] Error fetching unread DMs:", error));
  }, [currentUserId, apiUrl]);

  useEffect(() => {
    if (!currentUserId) return;
    axios
      .get(apiUrl("/api/groups/unread/count"), { withCredentials: true })
      .then((res) => setUnreadGroupCounts(res.data?.unreadCounts || {}))
      .catch((error) => console.error("[Messages] Error fetching unread groups:", error));
  }, [currentUserId, apiUrl]);

  useEffect(() => {
    if (!selectedFriendId) return;
    axios
      .get(apiUrl(`/api/direct-messages/${selectedFriendId}`), { withCredentials: true })
      .then((res) => {
        const list = res.data?.messages || [];
        setDirectMessagesByFriend((prev) => ({ ...prev, [selectedFriendId]: list }));
        setUnreadCounts((prev) => {
          const next = { ...prev };
          delete next[selectedFriendId];
          return next;
        });
        socketRef.current?.emit("mark-messages-read", { senderId: selectedFriendId });
        prevMessageCountRef.current = list.length;
        stickToBottomRef.current = true;
        scrollToBottom("auto");
      })
      .catch((error) => console.error("[Messages] Error fetching direct thread:", error));
  }, [selectedFriendId, apiUrl, socketRef, scrollToBottom]);

  useEffect(() => {
    if (!selectedGroupId) return;
    axios
      .get(apiUrl(`/api/groups/${selectedGroupId}/messages`), { withCredentials: true })
      .then((res) => {
        const list = res.data?.messages || [];
        setGroupMessages((prev) => ({ ...prev, [selectedGroupId]: list }));
        setUnreadGroupCounts((prev) => {
          const next = { ...prev };
          delete next[selectedGroupId];
          return next;
        });
        socketRef.current?.emit("join-group-room", { groupId: selectedGroupId });
        stickToBottomRef.current = true;
        scrollToBottom("auto");
      })
      .catch((error) => console.error("[Messages] Error fetching group thread:", error));
  }, [selectedGroupId, apiUrl, socketRef, scrollToBottom]);

  const activeMessages = isGroupChat
    ? groupMessages[selectedGroupId] || []
    : directMessagesByFriend[selectedFriendId] || [];

  useEffect(() => {
    const prev = prevMessageCountRef.current;
    const next = activeMessages.length;
    const added = next > prev;
    prevMessageCountRef.current = next;
    if (added && stickToBottomRef.current) scrollToBottom("auto");
  }, [activeMessages.length, scrollToBottom]);

  const handleSendMessage = useCallback(
    (e) => {
      e.preventDefault();
      const text = inputMessage.trim();
      if (!text || !socketRef.current) return;

      if (isGroupChat && selectedGroupId) {
        socketRef.current.emit("send-group-message", { groupId: selectedGroupId, message: text });
        socketRef.current.emit("group-typing-stop", { groupId: selectedGroupId });
      } else if (selectedFriendId) {
        socketRef.current.emit("send-message", { recipientId: selectedFriendId, message: text });
        socketRef.current.emit("typing-stop", { recipientId: selectedFriendId });
      } else {
        return;
      }

      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      setInputMessage("");
      setIsEmojiOpen(false);
      setIsGifOpen(false);
      stickToBottomRef.current = true;
      scrollToBottom("auto");
    },
    [
      inputMessage,
      socketRef,
      isGroupChat,
      selectedGroupId,
      selectedFriendId,
      scrollToBottom,
    ]
  );

  const handleInputChange = useCallback(
    (e) => {
      const val = e.target.value;
      setInputMessage(val);
      if (!socketRef.current) return;

      if (isGroupChat && selectedGroupId) {
        socketRef.current.emit("group-typing-start", { groupId: selectedGroupId });
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => {
          socketRef.current?.emit("group-typing-stop", { groupId: selectedGroupId });
        }, 1200);
      } else if (selectedFriendId) {
        socketRef.current.emit("typing-start", { recipientId: selectedFriendId });
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => {
          socketRef.current?.emit("typing-stop", { recipientId: selectedFriendId });
        }, 1200);
      }
    },
    [socketRef, isGroupChat, selectedGroupId, selectedFriendId]
  );

  const uploadChatFile = useCallback(
    async (file) => {
      if (!file || fileUploadBusy) return false;
      setFileUploadBusy(true);
      try {
        const form = new FormData();
        form.append("file", file);
        const cap = inputMessage.trim();
        if (cap) form.append("message", cap);

        if (isGroupChat && selectedGroupId) {
          const res = await axios.post(
            apiUrl(`/api/groups/${selectedGroupId}/messages/upload`),
            form,
            { withCredentials: true }
          );
          const msg = res.data;
          const gid = toFiniteId(msg?.group_id);
          if (gid != null) {
            setGroupMessages((prev) => {
              const cur = prev[gid] || [];
              if (cur.some((m) => Number(m.id) === Number(msg.id))) return prev;
              return { ...prev, [gid]: [...cur, msg] };
            });
          }
        } else if (selectedFriendId) {
          form.append("recipientId", String(selectedFriendId));
          const res = await axios.post(apiUrl("/api/direct-messages/upload"), form, {
            withCredentials: true,
          });
          const msg = res.data;
          const sid = Number(msg?.sender_id);
          const cid = Number(currentUserId);
          const otherId =
            Number.isFinite(sid) && Number.isFinite(cid) && sid === cid
              ? msg?.recipient_id
              : msg?.sender_id;
          appendDirectMessage(toFiniteId(otherId), msg);
        } else {
          return false;
        }

        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        setInputMessage("");
        setIsEmojiOpen(false);
        setIsGifOpen(false);
        if (socketRef.current) {
          if (isGroupChat && selectedGroupId) {
            socketRef.current.emit("group-typing-stop", { groupId: selectedGroupId });
          } else if (selectedFriendId) {
            socketRef.current.emit("typing-stop", { recipientId: selectedFriendId });
          }
        }
        stickToBottomRef.current = true;
        scrollToBottom("auto");
        return true;
      } catch (err) {
        console.error("[Messages] media upload failed:", err);
        return false;
      } finally {
        setFileUploadBusy(false);
      }
    },
    [
      fileUploadBusy,
      inputMessage,
      isGroupChat,
      selectedGroupId,
      selectedFriendId,
      apiUrl,
      currentUserId,
      appendDirectMessage,
      scrollToBottom,
    ]
  );

  const sendGifMessage = useCallback(
    (gifUrl) => {
      const url = String(gifUrl || "").trim();
      if (!url || !socketRef.current) return;

      if (isGroupChat && selectedGroupId) {
        socketRef.current.emit("send-group-message", { groupId: selectedGroupId, message: url });
        socketRef.current.emit("group-typing-stop", { groupId: selectedGroupId });
      } else if (selectedFriendId) {
        socketRef.current.emit("send-message", { recipientId: selectedFriendId, message: url });
        socketRef.current.emit("typing-stop", { recipientId: selectedFriendId });
      } else {
        return;
      }

      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      setInputMessage("");
      setIsEmojiOpen(false);
      setIsGifOpen(false);
      setGifQuery("");
      stickToBottomRef.current = true;
      scrollToBottom("auto");
    },
    [socketRef, isGroupChat, selectedGroupId, selectedFriendId, scrollToBottom]
  );

  const insertEmojiAtRange = useCallback((emoji, start, end) => {
    const value = String(emoji || "");
    setInputMessage((prev) => {
      const s = Number.isInteger(start) ? start : prev.length;
      const e = Number.isInteger(end) ? end : prev.length;
      return `${prev.slice(0, s)}${value}${prev.slice(e)}`;
    });
  }, []);

  useEffect(() => {
    if (!isGifOpen) return;
    if (!giphyKey) {
      setGifItems([]);
      setGifError("Missing REACT_APP_GIPHY_KEY");
      setGifLoading(false);
      return;
    }

    const q = gifQuery.trim();
    if (gifDebounceRef.current) clearTimeout(gifDebounceRef.current);
    gifDebounceRef.current = setTimeout(async () => {
      try {
        gifAbortRef.current?.abort?.();
      } catch {
        // ignore
      }
      const controller = new AbortController();
      gifAbortRef.current = controller;
      setGifLoading(true);
      setGifError("");
      try {
        const endpoint = q ? "search" : "trending";
        const data = await giphyFetch({
          key: giphyKey,
          endpoint,
          params: {
            q: q || undefined,
            limit: 24,
            offset: 0,
            rating: "pg-13",
            lang: "en",
          },
          signal: controller.signal,
        });
        const cleaned = data
          .map((g) => ({
            id: g?.id,
            title: g?.title || "",
            url: pickGiphyBestUrl(g),
          }))
          .filter((x) => x.id && x.url);
        setGifItems(cleaned);
      } catch (error) {
        if (String(error?.name) !== "AbortError") {
          setGifItems([]);
          setGifError("Could not load GIFs");
        }
      } finally {
        setGifLoading(false);
      }
    }, 260);

    return () => {
      if (gifDebounceRef.current) clearTimeout(gifDebounceRef.current);
    };
  }, [isGifOpen, gifQuery, giphyKey]);

  const toggleMemberSelection = useCallback((friendId) => {
    setSelectedGroupMembers((prev) =>
      prev.includes(friendId) ? prev.filter((id) => id !== friendId) : [...prev, friendId]
    );
  }, []);

  const handleCreateGroup = useCallback(async () => {
    if (!groupName.trim() || selectedGroupMembers.length === 0) return;
    try {
      const res = await axios.post(
        apiUrl("/api/groups"),
        { name: groupName.trim(), memberIds: selectedGroupMembers },
        { withCredentials: true }
      );
      const newGroup = res.data;
      setGroups((prev) => [newGroup, ...prev]);
      socketRef.current?.emit("join-group-room", { groupId: newGroup.group_id });
      setIsCreatingGroup(false);
      setGroupName("");
      setSelectedGroupMembers([]);
      setSelectedGroup(newGroup);
      setSelectedFriend(null);
    } catch (error) {
      console.error("[Messages] Error creating group:", error);
    }
  }, [groupName, selectedGroupMembers, apiUrl, socketRef]);

  const handleLeaveGroup = useCallback(async () => {
    if (!selectedGroup) return;
    const groupId = selectedGroup.group_id;
    try {
      socketRef.current?.emit("user-left-group", {
        groupId,
        username: effectiveUser?.username || "User",
      });
      socketRef.current?.emit("leave-group-room", { groupId });
      await axios.delete(apiUrl(`/api/groups/${groupId}`), { withCredentials: true });
      setGroups((prev) => prev.filter((g) => g.group_id !== groupId));
      setSelectedGroup(null);
      setGroupMessages((prev) => {
        const next = { ...prev };
        delete next[groupId];
        return next;
      });
    } catch (error) {
      console.error("[Messages] Error leaving group:", error);
    }
  }, [selectedGroup, apiUrl, socketRef, effectiveUser]);

  const handleDisbandGroup = useCallback(async () => {
    if (!selectedGroup) return;
    const groupId = selectedGroup.group_id;
    try {
      const res = await axios.post(
        apiUrl(`/api/groups/${groupId}/disband`),
        {},
        { withCredentials: true }
      );
      socketRef.current?.emit("group-disbanded", {
        groupId,
        memberIds: res.data.memberIds,
      });
      setGroups((prev) => prev.filter((g) => g.group_id !== groupId));
      setSelectedGroup(null);
      setGroupMessages((prev) => {
        const next = { ...prev };
        delete next[groupId];
        return next;
      });
    } catch (error) {
      console.error("[Messages] Error disbanding group:", error);
    }
  }, [selectedGroup, apiUrl, socketRef]);

  const inboxItems = useMemo(
    () =>
      buildInboxItems({
        friends,
        dmInbox,
        groups,
        onlineFriends,
        unreadCounts,
        unreadGroupCounts,
        selectedFriendId,
        selectedGroupId,
        directMessagesByFriend,
        groupMessages,
        query: searchQuery,
      }),
    [
      friends,
      dmInbox,
      groups,
      onlineFriends,
      unreadCounts,
      unreadGroupCounts,
      selectedFriendId,
      selectedGroupId,
      directMessagesByFriend,
      groupMessages,
      searchQuery,
    ]
  );

  const dmContactsById = useMemo(() => {
    const map = {};
    (friends || []).forEach((f) => {
      map[Number(f.user_id)] = f;
    });
    (dmInbox || []).forEach((row) => {
      const id = Number(row.user_id);
      if (!map[id]) {
        map[id] = {
          user_id: row.user_id,
          username: row.username || "Unknown",
          profile_picture: row.profile_picture || null,
        };
      }
    });
    return map;
  }, [friends, dmInbox]);

  return {
    effectiveUser,
    currentUserId,
    friends,
    dmInbox,
    dmContactsById,
    groups,
    inboxItems,
    searchQuery,
    setSearchQuery,
    selectedFriend,
    setSelectedFriend,
    selectedGroup,
    setSelectedGroup,
    selectedFriendId,
    selectedGroupId,
    isGroupChat,
    onlineFriends,
    typingUsers,
    groupTypingUsers,
    activeMessages,
    inputMessage,
    setInputMessage,
    handleInputChange,
    handleSendMessage,
    sendGifMessage,
    isEmojiOpen,
    setIsEmojiOpen,
    isGifOpen,
    setIsGifOpen,
    insertEmojiAtRange,
    gifQuery,
    setGifQuery,
    gifItems,
    gifLoading,
    gifError,
    messageListRef,
    computeStickToBottom,
    stickToBottomRef,
    formatMessageTime,
    isProbablyGifUrl,
    isCreatingGroup,
    setIsCreatingGroup,
    groupName,
    setGroupName,
    selectedGroupMembers,
    toggleMemberSelection,
    handleCreateGroup,
    handleLeaveGroup,
    handleDisbandGroup,
    uploadChatFile,
    fileUploadBusy,
    socketRef,
    games,
  };
}
